import { Button, Icon, Tabs, Tooltip } from '@infotech-soe/erp-ui-kit';
import { AuthMethod } from '@infotech-soe/merps/types/session';
import { DSAgents } from '@infotech-soe/mia-ds-service/types';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';

import { STORAGE_KEYS } from '../../../services/configuration/common';
import { AuthResult } from '../../../store/slices/session';

import {
  DSLoginFormContainer,
  KerberosFormContainer,
  LoginFormContainer,
  WindowsADFormContainer,
} from './AuthFormContainer';
import type { LoginWrapperContainerProps } from './LoginWrapperContainer';
import { baseAuthFormMessages, loginWrapperMessages } from './messages';
import { useLoginWrapperStyles } from './styles';

const getAvailableMethodO = (
  methods: LoginWrapperContainerProps['authMethods'],
  expected: AuthMethod
): AuthMethod | null => {
  const available = Object.entries(methods ?? {}).filter(
    ([, isActive]) => isActive
  ) as Array<[AuthMethod, boolean]>;

  if (!available.length) return null;

  if (available.some(([name]) => name === expected)) {
    return expected;
  }

  return available[0][0];
};

const tabName = (authMethod: AuthMethod, edsAgent?: DSAgents) =>
  `${authMethod}${edsAgent || ''}`;

const LoginForms: React.FC<
  Omit<LoginWrapperContainerProps, 'authenticated'>
> = ({
  dsAgents,
  hasError,
  requiredMethod,
  preferredAuthMethod,
  authMethods,
  serverTimeZone,
  connected,
}) => {
  const { formatMessage } = useIntl();
  const [activeTab, setActiveTab] = useState<string | null>(null);

  const handleTabActivate = (tab: string) => {
    if (tab.startsWith(AuthMethod.DIGITAL_SIGNATURE)) {
      const agent = tab.slice(AuthMethod.DIGITAL_SIGNATURE.length) as DSAgents;
      localStorage.setItem(STORAGE_KEYS.PREFERRED_DS_AGENT, agent);
    }
    setActiveTab(tab);
  };

  useEffect(() => {
    const firstAvailableAuthMethod = getAvailableMethodO(
      authMethods,
      requiredMethod || preferredAuthMethod || AuthMethod.LOGIN
    );
    if (firstAvailableAuthMethod === AuthMethod.DIGITAL_SIGNATURE) {
      const savedAgent = localStorage.getItem(
        STORAGE_KEYS.PREFERRED_DS_AGENT
      ) as DSAgents | null;
      const agent =
        savedAgent && dsAgents.includes(savedAgent) ? savedAgent : dsAgents[0];
      setActiveTab(tabName(AuthMethod.DIGITAL_SIGNATURE, agent));
    } else setActiveTab(firstAvailableAuthMethod);
  }, [requiredMethod, authMethods, preferredAuthMethod, dsAgents, connected]);

  const DSLoginForm: React.FC<{ dsAgent: DSAgents }> = ({ dsAgent }) => (
    <DSLoginFormContainer
      dsAgent={dsAgent}
      hasError={hasError}
      submitLabel={formatMessage(baseAuthFormMessages.submit)}
      withApplications
    />
  );

  const allowLP = useMemo(() => authMethods?.[AuthMethod.LOGIN], [authMethods]);

  const allowDS = useMemo(
    () => authMethods?.[AuthMethod.DIGITAL_SIGNATURE],
    [authMethods]
  );

  const allowWAD = useMemo(
    () => authMethods?.[AuthMethod.WINDOWS_AD],
    [authMethods]
  );

  const allowKRB = useMemo(
    () => authMethods?.[AuthMethod.KERBEROS],
    [authMethods]
  );

  if (!connected) {
    return (
      <>
        <p>{formatMessage(loginWrapperMessages.cannotConnectToServer)}</p>
        <p>{formatMessage(loginWrapperMessages.tryToReconnectLater)}</p>
      </>
    );
  }

  if (
    !serverTimeZone?.includes(Intl.DateTimeFormat().resolvedOptions().timeZone)
  ) {
    return formatMessage(loginWrapperMessages.wrongTimeZone, {
      tz: serverTimeZone?.join(', '),
    });
  }

  if (!activeTab || isEmpty(authMethods)) {
    return formatMessage(loginWrapperMessages.noAuthMethods);
  }

  if (allowLP && !allowDS && !allowWAD) {
    return <LoginFormContainer hasError={hasError} />;
  }

  if (!allowLP && allowDS && dsAgents.length === 1) {
    return <DSLoginForm dsAgent={dsAgents[0]} />;
  }

  return (
    <Tabs active={activeTab} onTabActivate={handleTabActivate}>
      <Tabs.Pane
        hidden={!allowLP}
        key={AuthMethod.LOGIN}
        name={AuthMethod.LOGIN}
        label={formatMessage(loginWrapperMessages[AuthMethod.LOGIN])}
      >
        <LoginFormContainer hasError={hasError} />
      </Tabs.Pane>

      <Tabs.Pane
        hidden={!allowDS || !dsAgents.includes(DSAgents.IIT)}
        key={tabName(AuthMethod.DIGITAL_SIGNATURE, DSAgents.IIT)}
        name={tabName(AuthMethod.DIGITAL_SIGNATURE, DSAgents.IIT)}
        label={`${formatMessage(
          loginWrapperMessages[AuthMethod.DIGITAL_SIGNATURE]
        )} ${DSAgents.IIT}`}
      >
        {allowDS && dsAgents.includes(DSAgents.IIT) && (
          <DSLoginForm dsAgent={DSAgents.IIT} />
        )}
      </Tabs.Pane>

      <Tabs.Pane
        hidden={!allowWAD && !allowKRB}
        key={allowKRB ? AuthMethod.KERBEROS : AuthMethod.WINDOWS_AD}
        name={allowKRB ? AuthMethod.KERBEROS : AuthMethod.WINDOWS_AD}
        label={formatMessage(loginWrapperMessages[AuthMethod.WINDOWS_AD])}
      >
        {allowKRB ? (
          <KerberosFormContainer hasError={hasError} />
        ) : (
          <WindowsADFormContainer hasError={hasError} />
        )}
      </Tabs.Pane>

      <Tabs.Pane
        hidden={!allowDS || !dsAgents.includes(DSAgents.CIPHER)}
        key={tabName(AuthMethod.DIGITAL_SIGNATURE, DSAgents.CIPHER)}
        name={tabName(AuthMethod.DIGITAL_SIGNATURE, DSAgents.CIPHER)}
        label={`${formatMessage(
          loginWrapperMessages[AuthMethod.DIGITAL_SIGNATURE]
        )} ${DSAgents.CIPHER}`}
      >
        {allowDS && dsAgents.includes(DSAgents.CIPHER) && (
          <DSLoginForm dsAgent={DSAgents.CIPHER} />
        )}
      </Tabs.Pane>
    </Tabs>
  );
};

const LoginWrapper: React.FC<LoginWrapperContainerProps> = ({
  authenticated,
  userGuideUrl,
  ...props
}) => {
  const classes = useLoginWrapperStyles();
  const { formatMessage } = useIntl();

  const handleUserGuideClick = () => {
    if (!userGuideUrl) return;
    window.open(userGuideUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={classes.wrapper}>
      {authenticated === AuthResult.NO ? (
        <>
          {userGuideUrl && (
            <div className={classes.userGuideButton}>
              <Tooltip label={formatMessage(loginWrapperMessages.userGuide)}>
                <Button variant="borderless" onClick={handleUserGuideClick}>
                  <Icon name="question-small" size={24} />
                </Button>
              </Tooltip>
            </div>
          )}
          <LoginForms {...props} />
        </>
      ) : (
        <Navigate to="/app" />
      )}
    </div>
  );
};

export default LoginWrapper;
